import {
    getFilterOptionsFromClassifier,
    getSortedFilterOptions,
    mapToFilterOptions,
} from 'components/tables/list-table/filterUtil.ts';
import {
    FilterConfig,
    MultiselectFilterForm,
    QuickFilterCheckboxGroupForm,
    QuickFilterForm,
    SelectOption,
} from 'components/tables/list-table/ListTableTypes';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts.ts';
import {
    useLazyGetAllEmployeesListWithFiltersQuery,
    useLazyGetEmployeesListWithFiltersQuery,
} from 'store/api/employees.ts';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups.ts';
import { useLazyGetMattersWithFiltersQuery } from 'store/api/matters.ts';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetPracticeAreasWithFiltersQuery } from 'store/api/practice-areas.ts';
import { FeatureFlag } from 'types/features.ts';
import { ExternalStatusType, InvoiceStatusType } from 'types/invoices';
import { PricingLimitationType } from 'types/matters.ts';
import { PracticeAreaType } from 'types/practice-areas.ts';
import {
    enrichDynamicFiltersWithPassiveFilter,
    filterMainFilters,
    PaginatedFieldPassiveFilter,
} from 'utils/filterUtil.ts';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { ClassifierData } from 'types/classifiers';
import { DocumentTemplateMinDto } from 'types/document';

/** The classifiers the invoices list reads, its filters included. */
export const INVOICE_LIST_CLASSIFIERS = [
    'ACCOUNTING_REVENUE_TYPE',
    'BUSINESS_AREA_TYPE',
    'INVOICE_TYPE',
    'INVOICE_STATUS_TYPE',
    'CURRENCY',
    'INVOICING_INTERVAL_TYPE',
    'COUNTRY',
    'GROUP_TYPE',
    'PRACTICE_AREA_TYPE',
    'PRICING_LIMITATION_TYPE',
    'NEXT_DEBT_COLLECTION_ACTION_TYPE',
    'RECEIVABILITY_TYPE',
];

const READ_COMMENTS_PERMISSION = ['READ.INVOICES.COMMENTS'];

const filterStatusTypes = [
    InvoiceStatusType.DRAFT,
    InvoiceStatusType.REVIEWED,
    InvoiceStatusType.APPROVED,
    InvoiceStatusType.FINALIZED,
    InvoiceStatusType.SENT,
    InvoiceStatusType.PAID,
    InvoiceStatusType.CREDITED,
];

const pricingLimitationTypes = [
    PricingLimitationType.HOURLY_CAPPED,
    PricingLimitationType.FIXED_ONETIME,
    PricingLimitationType.HOURLY_UNLIMITED,
    PricingLimitationType.FIXED_RECURRING,
    PricingLimitationType.PRO_BONO,
];

// Add more if needed
export type FilterId = 'matterIdInFilter';
export type FilterGroup = 'invoices.filter.client-group' | 'invoices.filter.case-group' | 'invoices.filter.other-group';

export const KSEF_STATUS_TRANSLATION_KEYS: Record<string, string> = {
    NOT_SENT: 'invoices.ksef.status.not-sent',
    IGNORE: 'invoices.ksef.status.exempt',
    REJECTED: 'invoices.ksef.status.rejected',
    SKIPPED: 'invoices.ksef.status.skipped',
    APPROVED: 'invoices.ksef.status.approved',
    PENDING: 'invoices.ksef.status.pending',
};

export const KSEF_EXTERNAL_ACTION_TRANSLATION_KEYS: Record<string, string> = {
    ERROR: 'invoices.ksef.external-action.error',
    REJECTED: 'invoices.ksef.external-action.rejected',
    APPROVED: 'invoices.ksef.external-action.approved',
    PENDING: 'invoices.ksef.external-action.pending',
};

export const KSEF_EXTERNAL_STATE_TRANSLATION_KEYS: Record<string, string> = {
    REJECTED: 'invoices.ksef.external-state.rejected',
    Registered: 'invoices.ksef.external-state.registered',
};

type Args = {
    classifiers: Record<string, ClassifierData>;
    passiveFilter?: string;
    invoiceTemplateList?: DocumentTemplateMinDto[];
    disabledGroups?: FilterGroup[];
    disabledFilterIds?: FilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<FilterId>[];
    /** Whether the list has its KSeF view, and with it the KSeF filters. */
    isKsefTabEnabled: boolean;
};

/**
 * The invoices list's filters: its quick filters, the fields of its filter panel, what its search reads and the
 * filter it always applies. One definition for the list and for whatever else filters invoices by the same fields.
 */
export function useInvoiceFilterConfig({
    classifiers,
    passiveFilter = '',
    invoiceTemplateList,
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
    isKsefTabEnabled,
}: Args): FilterConfig {
    const isInvoicingDateOnPartyFormEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_DATE_ON_PARTY_FORM);
    const isExcludeFromQlikSenseEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_EXCLUDE_FROM_QLIK_SENSE);
    const canReadComments = useHasPermissions(READ_COMMENTS_PERMISSION);

    const ksefStatusOptions = useMemo<SelectOption[]>(
        () =>
            Object.values(ExternalStatusType).map((code) => ({
                label: t(KSEF_STATUS_TRANSLATION_KEYS[code] ?? code),
                value: code,
            })),
        []
    );

    const ksefExternalActionOptions = useMemo<SelectOption[]>(
        () =>
            Object.keys(KSEF_EXTERNAL_ACTION_TRANSLATION_KEYS).map((code) => ({
                label: t(KSEF_EXTERNAL_ACTION_TRANSLATION_KEYS[code]),
                value: code,
            })),
        []
    );

    const ksefExternalStateOptions = useMemo<SelectOption[]>(
        () =>
            Object.keys(KSEF_EXTERNAL_STATE_TRANSLATION_KEYS).map((code) => ({
                label: t(KSEF_EXTERNAL_STATE_TRANSLATION_KEYS[code]),
                value: code,
            })),
        []
    );

    const getFormattedDateWithDaysBefore = (days: number) => {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return format(date, DEFAULT_DATE_FORMAT);
    };

    const invoiceStatusTypeOptions = useMemo(
        () => mapToFilterOptions(filterStatusTypes, classifiers['INVOICE_STATUS_TYPE']),
        [classifiers]
    );

    const pricingLimitationTypeOptions = useMemo(
        () => mapToFilterOptions(pricingLimitationTypes, classifiers['PRICING_LIMITATION_TYPE']),
        [classifiers]
    );

    const accountingRevenueTypeOptions = useMemo(
        () => (classifiers ? getFilterOptionsFromClassifier(classifiers?.['ACCOUNTING_REVENUE_TYPE']) : []),
        [classifiers]
    );

    const nextDebtCollectionActionTypeOptions = useMemo(
        () => (classifiers ? getFilterOptionsFromClassifier(classifiers?.['NEXT_DEBT_COLLECTION_ACTION_TYPE']) : []),
        [classifiers]
    );

    const receivabilityTypeOptions = useMemo(
        () =>
            (classifiers ? getFilterOptionsFromClassifier(classifiers?.['RECEIVABILITY_TYPE']) : []).map((option) => ({
                label: option.label,
                argument: option.value,
            })),
        [classifiers]
    );

    // Some statuses are hidden for users, and the uninvoiced filter is hidden until it is reworked.
    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'invoiceStatusTypeCode',
                    chipLabel: 'filters.status',
                    filters: invoiceStatusTypeOptions,
                    multiple: true,
                    onChange: ({ isSelected, value, groupSelector, mainFilterState, handleMainFilterStateChange }) => {
                        if (!value) return;

                        const newMainState = { ...mainFilterState };
                        const mainFilterStateElement = newMainState[groupSelector];

                        if (mainFilterStateElement?.type !== 'multiselect') return;

                        const mainF = mainFilterStateElement as MultiselectFilterForm;

                        if (isSelected) {
                            const addedValue = invoiceStatusTypeOptions?.find((item) => item.value === value);
                            if (addedValue) {
                                mainF.selected.push({ ...addedValue });
                                handleMainFilterStateChange(newMainState);
                            }
                        } else {
                            mainF.selected = mainF.selected?.filter((item) => item.value !== value) || [];
                            handleMainFilterStateChange(newMainState);
                        }
                    },
                },
                {
                    selector: 'id',
                    comperator: '=ismyinvoicematter=',
                    filters: [
                        {
                            tooltip: 'invoices.filter.my-cases-tooltip',
                            label: t('invoices.filter.my-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'id',
                    comperator: '=ismyteaminvoicematter=',
                    filters: [
                        {
                            tooltip: 'invoices.filter.my-team-cases-tooltip',
                            label: t('invoices.filter.my-team-cases'),
                            argument: 'true',
                        },
                    ],
                },
            ],
            filters: enrichDynamicFiltersWithPassiveFilter(
                filterMainFilters(
                    [
                        // -------- INVOICE --------
                        {
                            type: 'dynamicmultiselect',
                            label: t('invoices.filter.invoiced-office'),
                            selector: 'invoicedByOffice.id',
                            group: 'invoices.filter.invoice-group',
                            paginated: {
                                fetch: useLazyGetMinOfficesWithFiltersQuery,
                                searchField: 'name',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                            getOptionEndAdornment: officeInactiveSuffix,
                        },
                        {
                            type: 'daterange',
                            selector: 'invoiceDate',
                            group: 'invoices.filter.invoice-group',
                            label: t('invoices.filter.invoice-date-from'),
                            labelTo: t('invoices.filter.invoice-date-until'),
                            shouldDisplayQuickPick: true,
                        },
                        {
                            type: 'daterange',
                            selector: 'createdDate',
                            group: 'invoices.filter.invoice-group',
                            label: t('invoices.filter.invoice-created-from'),
                            labelTo: t('invoices.filter.invoice-created-until'),
                            shouldDisplayQuickPick: true,
                        },
                        {
                            type: 'dynamicmultiselect',
                            label: t('invoices.filter.created-by'),
                            selector: 'createdByEmployee.id',
                            group: 'invoices.filter.invoice-group',
                            paginated: {
                                // Every employee, not only the active ones: a leaver's invoices stay findable.
                                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'createdByEmployee.id',
                            group: 'invoices.filter.invoice-group',
                            comperator: '=isnull=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.created-by-system-tooltip',
                                    label: t('invoices.filter.created-by-system'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'daterange',
                            selector: 'paidOnDate',
                            group: 'invoices.filter.invoice-group',
                            label: t('invoices.filter.paid-date-from'),
                            labelTo: t('invoices.filter.paid-date-until'),
                            shouldDisplayQuickPick: true,
                        },
                        {
                            type: 'daterange',
                            selector: 'lastPaymentDate',
                            group: 'invoices.filter.invoice-group',
                            label: t('invoices.filter.last-payment-from'),
                            labelTo: t('invoices.filter.last-payment-until'),
                            shouldDisplayQuickPick: true,
                        },
                        {
                            type: 'interval',
                            label: t('invoices.filter.total-amount'),
                            selector: 'totalAmountInPaymentCurrency',
                            group: 'invoices.filter.invoice-group',
                            minLabel: t('invoices.filter.total-amount-lowest'),
                            maxLabel: t('invoices.filter.total-amount-highest'),
                        },
                        {
                            type: 'interval',
                            label: t('invoices.filter.outstanding-balance-amount'),
                            selector: 'remainingAmountInPaymentCurrency',
                            group: 'invoices.filter.invoice-group',
                            minLabel: t('invoices.filter.outstanding-balance-lowest'),
                            maxLabel: t('invoices.filter.outstanding-balance-highest'),
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.invoice-currency'),
                            selector: 'invoiceCurrencyTypeCode',
                            group: 'invoices.filter.invoice-group',
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                            tooltip: 'invoices.tooltips.invoice-currency-refers-to-price-list-currency',
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.payment-currency'),
                            selector: 'paymentCurrencyTypeCode',
                            group: 'invoices.filter.invoice-group',
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                            tooltip: 'invoices.tooltips.payment-currency-is-expected-to-be-paid-currency',
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.invoice-status'),
                            selector: 'invoiceStatusTypeCode',
                            group: 'invoices.filter.invoice-group',
                            options: invoiceStatusTypeOptions,
                            onChange: (val, groupSelector, quickFilters: QuickFilterForm, handleQuickFilterChange) => {
                                const selectedValues = val?.map((item) => item.value);
                                Object.values(quickFilters).map((quickFilter) => {
                                    if (quickFilter.selector == 'invoiceStatusTypeCode') {
                                        const checkboxGroup = quickFilter as QuickFilterCheckboxGroupForm;
                                        checkboxGroup.filters?.map((qf) => {
                                            if (!selectedValues?.includes(qf.argument) && qf.isSelected) {
                                                handleQuickFilterChange(false, groupSelector, qf.key);
                                            }
                                            if (selectedValues?.includes(qf.argument) && !qf.isSelected) {
                                                handleQuickFilterChange(true, groupSelector, qf.key);
                                            }
                                        });
                                    }
                                });
                            },
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.invoice-type'),
                            selector: 'invoiceTypeCode',
                            group: 'invoices.filter.invoice-group',
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['INVOICE_TYPE']) : [],
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.invoice-interval'),
                            selector: 'matter.invoicingIntervalTypeCode',
                            group: 'invoices.filter.invoice-group',
                            options: classifiers
                                ? getFilterOptionsFromClassifier(classifiers?.['INVOICING_INTERVAL_TYPE'])
                                : [],
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.invoice-template'),
                            selector: 'invoiceTemplate.id',
                            group: 'invoices.filter.invoice-group',
                            options: getSortedFilterOptions('name', 'id', invoiceTemplateList ?? []),
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.accounting-revenue-type'),
                            selector: 'accountingRule.accountingRevenueTypeCode',
                            group: 'invoices.filter.invoice-group',
                            options: accountingRevenueTypeOptions,
                            getShouldHide: () => accountingRevenueTypeOptions.length <= 1,
                        },
                        {
                            type: 'number',
                            label: t('invoices.filter.days-overdue'),
                            // The backend derives the same subtraction the "Days overdue" column shows, so
                            // typing 10 keeps every invoice that column reads as 10d or more. Invoices with
                            // no due date have no days overdue and drop out.
                            selector: 'daysOverdue',
                            comperator: '=ge=',
                            group: 'invoices.filter.invoice-group',
                            min: 0,
                            tooltip: 'invoices.tooltips.days-overdue',
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.next-debt-collection-action'),
                            selector: 'debtCollection.nextDebtCollectionActionTypeCode',
                            group: 'invoices.filter.invoice-group',
                            options: nextDebtCollectionActionTypeOptions,
                        },
                        {
                            type: 'checkboxgroup',
                            label: t('invoices.receivability'),
                            selector: 'receivabilityTypeCode',
                            group: 'invoices.filter.invoice-group',
                            comperator: '=in=',
                            multiple: true,
                            filters: receivabilityTypeOptions,
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'invoiceStatusTypeCode',
                            group: 'invoices.filter.invoice-group',
                            comperator: '=invoiceremainsunpaid=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.outstanding-balance-tooltip',
                                    label: t('invoices.filter.outstanding-balance'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'dueDate',
                            group: 'invoices.filter.invoice-group',
                            comperator: '=duedateispast=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.due-date-past-tooltip',
                                    label: t('invoices.filter.due-date-past'),
                                    argument: format(new Date(), DEFAULT_DATE_FORMAT),
                                },

                                {
                                    tooltip: 'invoices.filter.due-date-week-tooltip',
                                    label: t('invoices.filter.due-date-week'),
                                    argument: getFormattedDateWithDaysBefore(7),
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'debtCollection.nextReminderDate',
                            group: 'invoices.filter.invoice-group',
                            comperator: '=le=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.reminder-date-arrived-tooltip',
                                    label: t('invoices.filter.reminder-date-arrived'),
                                    argument: format(new Date(), DEFAULT_DATE_FORMAT),
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            label: t('invoices.filter.comments'),
                            selector: 'id',
                            group: 'invoices.filter.invoice-group',
                            comperator: '=hascomments=',
                            multiple: false,
                            filters: [
                                {
                                    tooltip: 'invoices.filter.comments-all-tooltip',
                                    label: t('invoices.filter.comments-all'),
                                    argument: 'all',
                                },
                                {
                                    tooltip: 'invoices.filter.comments-open-tooltip',
                                    label: t('invoices.filter.comments-open'),
                                    argument: 'open',
                                },
                            ],
                            getShouldHide: () => !canReadComments,
                        },
                        // -------- CLIENT --------
                        {
                            type: 'dynamicmultiselect',
                            subType: 'contact',
                            group: 'invoices.filter.client-group',
                            label: t('invoices.filter.client-name'),
                            selector: 'invoiceMatters.matter.clientParty.id',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetMinContactsListWithFiltersQuery,
                                searchField: ['name', 'partyNumber'],
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'dynamicmultiselect',
                            group: 'invoices.filter.client-group',
                            label: t('invoices.filter.relationship-partner'),
                            selector: 'invoiceMatters.matter.clientParty.partners.employee.id',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'dynamicmultiselect',
                            group: 'invoices.filter.client-group',
                            label: t('invoices.filter.relationship-manager'),
                            selector: 'invoiceMatters.matter.clientParty.managers.employee.id',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.country'),
                            selector: 'invoiceMatters.matter.clientParty.addresses.countryCode',
                            group: 'invoices.filter.client-group',
                            canExclude: true,
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                        },
                        {
                            type: 'dynamicmultiselect',
                            label: t('invoices.filter.client-group-label'),
                            selector: 'invoiceMatters.matter.clientParty.partyGroupParties.partyGroup.id',
                            group: 'invoices.filter.client-group',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetMinGroupsListWithFiltersQuery,
                                searchField: 'groupName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'groupName',
                            },
                        },
                        {
                            type: 'text',
                            label: t('invoices.filter.e-billing-system-url-equals'),
                            selector: 'clientParty.externalBillingInfo',
                            group: 'invoices.filter.client-group',
                            tooltip: 'invoices.filter.e-billing-system-url-equals-tooltip',
                        },

                        // -------- CASE --------
                        {
                            type: 'checkboxgroup',
                            selector: 'invoiceMatters.matter',
                            group: 'invoices.filter.case-group',
                            comperator: '=ismyinvoicematter=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.my-cases-tooltip',
                                    label: t('invoices.filter.my-cases'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'invoiceMatters.matter',
                            group: 'invoices.filter.case-group',
                            comperator: '=ismyteaminvoicematter=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.my-team-cases-tooltip',
                                    label: t('invoices.filter.my-team-cases'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            id: 'matterIdInFilter',
                            type: 'dynamicmultiselect',
                            group: 'invoices.filter.case-group',
                            label: t('invoices.filter.case-name-number'),
                            selector: 'invoiceMatters.matter.id',
                            canExclude: true,
                            getOptionStartAdornment: renderMatterIndicators,
                            paginated: {
                                fetch: useLazyGetMattersWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'fullName',
                            },
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.fee-arrangement'),
                            selector: 'invoiceMatters.matter.pricingLimitationTypeCode',
                            group: 'invoices.filter.case-group',
                            options: pricingLimitationTypeOptions,
                        },
                        {
                            type: 'dynamicmultiselect',
                            group: 'invoices.filter.case-group',
                            label: t('invoices.filter.supervising-person'),
                            selector: 'invoiceMatters.matter.supervisedByEmployee.id',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'dynamicmultiselect',
                            group: 'invoices.filter.case-group',
                            label: t('invoices.filter.responsible-person'),
                            selector: 'invoiceMatters.matter.responsibleEmployee.id',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'dynamicmultiselect',
                            group: 'invoices.filter.case-group',
                            label: t('invoices.filter.practice-area'),
                            selector: 'invoiceMatters.matter.practiceAreas.practiceArea.id',
                            tooltip: 'invoices.filter.practice-area-tooltip',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetPracticeAreasWithFiltersQuery,
                                searchField: 'name',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                                filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}`,
                            },
                        },
                        {
                            type: 'text',
                            label: t('invoices.filter.matter-reference'),
                            selector: 'invoiceMatters.matter.matterClientReference',
                            group: 'invoices.filter.case-group',
                        },
                        // -------- PAYER --------
                        {
                            type: 'dynamicmultiselect',
                            subType: 'contact',
                            group: 'invoices.filter.payer-group',
                            label: t('invoices.filter.payer-name'),
                            selector: 'clientParty.id',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetMinContactsListWithFiltersQuery,
                                searchField: ['name', 'partyNumber'],
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'multiselect',
                            group: 'invoices.filter.payer-group',
                            label: t('invoices.filter.payer-business-areas'),
                            selector: 'clientParty.businessAreas.businessAreaCode',
                            canExclude: true,
                            options: classifiers
                                ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE'])
                                : [],
                        },
                        {
                            type: 'dynamicmultiselect',
                            label: t('invoices.filter.payer-group-label'),
                            selector: 'clientParty.partyGroupParties.partyGroup.id',
                            group: 'invoices.filter.payer-group',
                            canExclude: true,
                            paginated: {
                                fetch: useLazyGetMinGroupsListWithFiltersQuery,
                                searchField: 'groupName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'groupName',
                            },
                        },
                        {
                            type: 'multiselect',
                            label: t('invoices.filter.country'),
                            selector: 'clientParty',
                            comperator: '=billingaddresscountry=',
                            group: 'invoices.filter.payer-group',
                            canExclude: true,
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                        },
                        {
                            type: 'dropdown',
                            label: t('invoices.filter.deadline-day'),
                            selector: 'clientParty.invoicingDeadlineDay',
                            group: 'invoices.filter.payer-group',
                            comperator: '=invoicingdeadlineday=',
                            options: [
                                { label: 'Date missed', value: -1 },
                                { label: 'Today', value: new Date().getDate() },
                                ...Array.from({ length: 31 }, (_, i) => ({
                                    label: (i + 1).toString(),
                                    value: i + 1,
                                })),
                            ],
                        },
                        // -------- OTHER --------

                        {
                            type: 'checkboxgroup',
                            selector: 'id',
                            comperator: '=isjointinvoice=',
                            group: 'invoices.filter.other-group',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.joint-invoice-tooltip',
                                    label: t('invoices.filter.joint-invoice'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'isSplit',
                            group: 'invoices.filter.other-group',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.split-invoice-tooltip',
                                    label: t('invoices.filter.split-invoice'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'invoiceStatusTypeCode',
                            group: 'invoices.filter.other-group',
                            comperator: '=onlypostponed=',
                            onChange: (option, _, quickFilters, handleQuickFilterChange) => {
                                const { value } = option as SelectOption;
                                if (!value) {
                                    return;
                                }
                                Object.values(quickFilters).forEach((quickFilter) => {
                                    if (quickFilter.selector !== 'invoiceStatusTypeCode') {
                                        return quickFilter;
                                    }
                                    const checkboxGroup = quickFilter as QuickFilterCheckboxGroupForm;
                                    checkboxGroup.filters?.forEach((qf, index) => {
                                        const shouldFetch = (checkboxGroup.filters?.length ?? 0) === index + 1;
                                        handleQuickFilterChange(
                                            false,
                                            'invoiceStatusTypeCode:=in=',
                                            qf.key,
                                            shouldFetch
                                        );
                                    });
                                });
                            },
                            filters: [
                                {
                                    tooltip: 'invoices.filter.postponed-invoice-tooltip',
                                    label: t('invoices.filter.postponed-invoice'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'clientParty.externalBillingInfo',
                            group: 'invoices.filter.other-group',
                            comperator: '=isnotempty=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.e-billing-system-url-is-set-tooltip',
                                    label: t('invoices.filter.e-billing-system-url-is-set'),
                                    argument: 'true',
                                },
                            ],
                        },
                        ...(isInvoicingDateOnPartyFormEnabled
                            ? [
                                  {
                                      type: 'checkboxgroup' as const,
                                      selector: 'clientParty.issueInvoicesWithCurrentDate',
                                      group: 'invoices.filter.payer-group' as const,
                                      filters: [
                                          {
                                              label: t('invoices.filter.invoice-date-current-date'),
                                              argument: 'true',
                                          },
                                      ],
                                  },
                              ]
                            : []),
                        ...(isExcludeFromQlikSenseEnabled
                            ? [
                                  {
                                      type: 'checkboxgroup' as const,
                                      selector: 'excludeFromReporting',
                                      group: 'invoices.filter.other-group' as const,
                                      comperator: '=isnottrue=',
                                      filters: [
                                          {
                                              tooltip: 'invoices.filter.exclude-from-report-tooltip',
                                              label: t('invoices.filter.exclude-from-report'),
                                              argument: 'true',
                                          },
                                      ],
                                  },
                              ]
                            : []),
                        {
                            type: 'checkboxgroup',
                            selector: 'invoiceMatters.matter.supervisedByEmployee.id',
                            group: 'invoices.filter.other-group',
                            comperator: '=isinactivesupervisingemployee=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.inactive-supervising-partner-tooltip',
                                    label: t('invoices.filter.inactive-supervising-partner'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'invoiceMatters.matter.responsibleEmployee.id',
                            group: 'invoices.filter.other-group',
                            comperator: '=isinactiveresponsiblelawyer=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.inactive-responsible-lawyer-tooltip',
                                    label: t('invoices.filter.inactive-responsible-lawyer'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'id',
                            group: 'invoices.filter.other-group',
                            comperator: '=includeOldInvoices=',
                            filters: [
                                {
                                    tooltip: 'invoices.filter.include-old-invoices-tooltip',
                                    label: t('invoices.filter.include-old-invoices'),
                                    argument: 'true',
                                },
                            ],
                        },
                        // -------- KSeF --------
                        ...(isKsefTabEnabled
                            ? [
                                  {
                                      type: 'checkboxgroup' as const,
                                      selector: 'externalStatusTypeCode',
                                      group: 'invoices.filter.ksef-group' as const,
                                      comperator: '!=',
                                      filters: [
                                          {
                                              tooltip: 'invoices.filter.ksef-exclude-exempt-tooltip',
                                              label: t('invoices.filter.ksef-exclude-exempt'),
                                              argument: ExternalStatusType.IGNORE,
                                          },
                                      ],
                                  },
                                  {
                                      type: 'multiselect' as const,
                                      label: t('invoices.filter.ksef-status'),
                                      selector: 'externalStatusTypeCode',
                                      group: 'invoices.filter.ksef-group' as const,
                                      options: ksefStatusOptions,
                                  },
                                  {
                                      type: 'multiselect' as const,
                                      label: t('invoices.filter.ksef-external-status'),
                                      selector: 'externalEvents.actionTypeCode',
                                      group: 'invoices.filter.ksef-group' as const,
                                      options: ksefExternalActionOptions,
                                  },
                                  {
                                      type: 'text' as const,
                                      label: t('invoices.filter.ksef-external-invoice-number'),
                                      selector: 'externalEvents.externalInvoiceNumber',
                                      group: 'invoices.filter.ksef-group' as const,
                                  },
                                  {
                                      type: 'text' as const,
                                      label: t('invoices.filter.ksef-external-id'),
                                      selector: 'externalEvents.externalId',
                                      group: 'invoices.filter.ksef-group' as const,
                                  },
                                  {
                                      type: 'multiselect' as const,
                                      label: t('invoices.filter.ksef-external-state'),
                                      selector: 'externalEvents.externalInvoiceState',
                                      group: 'invoices.filter.ksef-group' as const,
                                      options: ksefExternalStateOptions,
                                  },
                                  {
                                      type: 'text' as const,
                                      label: t('invoices.filter.ksef-external-state-reason'),
                                      selector: 'externalEvents.externalInvoiceStateReason',
                                      group: 'invoices.filter.ksef-group' as const,
                                      comperator: '=ilike=',
                                  },
                              ]
                            : []),
                    ],
                    disabledGroups,
                    disabledFilterIds
                ),
                paginatedFieldPassiveFilters
            ),
            searchBarSelectors: [
                { selector: 'invoiceNumber' },
                { selector: 'clientParty.name' },
                { selector: 'invoiceMatters.matter.clientParty.name' },
                { selector: 'invoiceMatters.matter.caseNumber' },
                { selector: 'invoiceMatters.matter.name' },
                { selector: 'invoiceMatters.matter.matterClientReference' },
                { selector: 'invoiceMatters.matter.primaryContactParty.name' },
            ],
            passiveFilter: passiveFilter,
        }),
        [
            classifiers,
            invoiceStatusTypeOptions,
            passiveFilter,
            invoiceTemplateList,
            pricingLimitationTypeOptions,
            accountingRevenueTypeOptions,
            nextDebtCollectionActionTypeOptions,
            receivabilityTypeOptions,
            disabledGroups,
            disabledFilterIds,
            paginatedFieldPassiveFilters,
            isKsefTabEnabled,
            isInvoicingDateOnPartyFormEnabled,
            isExcludeFromQlikSenseEnabled,
            canReadComments,
            ksefStatusOptions,
            ksefExternalActionOptions,
            ksefExternalStateOptions,
        ]
    );

    return filterConfig;
}
